import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  getModels,
  getTenderModels,
  type ModelStatus,
  type TenderEffortKey,
  type TenderModelKey,
  type TenderModelUpdates,
  updateTenderModels,
} from "@/api/admin-api"
import { Zap } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"

import { AdminSettingsLoadState } from "./admin-settings-load-state"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { TenderHandoffConfigContent } from "./tender-handoff-config-card"
import { adminQueryKeys } from "../hooks/admin-query-keys"

const SYSTEM_DEFAULT_VALUE = "__system_default__"

const TENDER_MODEL_FIELDS = [
  {
    key: "tender_extraction_model_id",
    effortKey: "tender_extraction_reasoning_effort",
    id: "tender-extraction-model",
    labelKey: "adminDashboard.tenderModels.extractionModel",
    labelDefault: "Extraction Model",
    descriptionKey: "adminDashboard.tenderModels.extractionModelDescription",
    descriptionDefault: "Used for Phase 0 classification and Phase 1 extraction",
  },
  {
    key: "tender_fast_model_id",
    effortKey: "tender_fast_reasoning_effort",
    id: "tender-fast-model",
    labelKey: "adminDashboard.tenderModels.fastModel",
    labelDefault: "Fast Model",
    descriptionKey: "adminDashboard.tenderModels.fastModelDescription",
    descriptionDefault: "Used for Phase 2 verification and synthesis",
  },
  {
    key: "tender_cv_matching_model_id",
    effortKey: "tender_cv_matching_reasoning_effort",
    id: "tender-cv-matching-model",
    labelKey: "adminDashboard.tenderModels.cvMatchingModel",
    labelDefault: "CV Matching Model",
    descriptionKey: "adminDashboard.tenderModels.cvMatchingModelDescription",
    descriptionDefault: "Used to classify requirements and match candidates. EU deployments only.",
  },
  {
    key: "tender_bid_review_model_id",
    effortKey: "tender_bid_review_reasoning_effort",
    id: "tender-bid-review-model",
    labelKey: "adminDashboard.tenderModels.bidReviewModel",
    labelDefault: "Bid Review Model",
    descriptionKey: "adminDashboard.tenderModels.bidReviewModelDescription",
    descriptionDefault: "Used to analyse submitted bids against the tender",
  },
  {
    key: "tender_chat_model_id",
    effortKey: "tender_chat_reasoning_effort",
    id: "tender-chat-model",
    labelKey: "adminDashboard.tenderModels.chatModel",
    labelDefault: "Tender Chat Model",
    descriptionKey: "adminDashboard.tenderModels.chatModelDescription",
    descriptionDefault:
      "Used by the assistant within a tender case. EU deployments only — the assistant can read case CVs.",
  },
] as const

function isEuModel(model: ModelStatus): boolean {
  return model.id.toLowerCase().endsWith("_eu") || model.id.toLowerCase().endsWith("_dzone")
}

function TenderModelSelect({
  id,
  label,
  description,
  value,
  models,
  onValueChange,
  systemDefaultLabel,
  formatUnavailable,
  disabled,
}: {
  id: string
  label: string
  description: string
  value: string
  models: ModelStatus[]
  onValueChange: (value: string) => void
  systemDefaultLabel: string
  formatUnavailable: (modelId: string) => string
  disabled: boolean
}) {
  const unavailableModel =
    value && value !== SYSTEM_DEFAULT_VALUE && !models.some((model) => model.id === value)

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-muted-foreground text-xs">{description}</p>
      <Select
        disabled={disabled}
        value={value || SYSTEM_DEFAULT_VALUE}
        onValueChange={onValueChange}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SYSTEM_DEFAULT_VALUE}>{systemDefaultLabel}</SelectItem>
          {unavailableModel && (
            <SelectItem value={value}>
              <span>{formatUnavailable(value)}</span>
            </SelectItem>
          )}
          {models.map((model) => (
            <SelectItem key={model.id} value={model.id}>
              <span>{model.displayName}</span>
              <span className="text-muted-foreground ml-2 text-xs">{model.id}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function TenderModelConfigPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [modelUpdates, setModelUpdates] = useState<TenderModelUpdates>({})

  const {
    data: tenderConfig,
    isLoading: configLoading,
    isFetching: configFetching,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: adminQueryKeys.tenderModels(),
    queryFn: getTenderModels,
    staleTime: 60_000,
    select: (data) => {
      // Initialize local state from server data on first load
      return data
    },
  })

  const {
    data: models,
    isLoading: modelsLoading,
    isFetching: modelsFetching,
    refetch: refetchModels,
  } = useQuery({
    queryKey: ["admin", "models"],
    queryFn: getModels,
    staleTime: 60_000,
  })

  const enabledModels = models?.filter((model) => model.enabled) ?? []
  const euModels = enabledModels.filter(isEuModel)
  const EU_ONLY_KEYS: readonly TenderModelKey[] = [
    "tender_cv_matching_model_id",
    "tender_chat_model_id",
  ]
  const hasChanges = Object.keys(modelUpdates).length > 0

  function getCurrentModelId(key: TenderModelKey, updates = modelUpdates): string {
    return updates[key] ?? tenderConfig?.[key] ?? ""
  }

  function getCurrentEffort(key: TenderEffortKey, updates = modelUpdates): string {
    return updates[key] ?? tenderConfig?.[key] ?? ""
  }

  function getEffectiveModelId(key: TenderModelKey, updates = modelUpdates): string {
    function usable(modelId: string): boolean {
      return (
        enabledModels.some(
          (model) => model.id === modelId && (!EU_ONLY_KEYS.includes(key) || isEuModel(model))
        ) && Boolean(tenderConfig?.reasoning_profiles[modelId])
      )
    }
    const selected = getCurrentModelId(key, updates)
    if (selected && usable(selected)) return selected
    if (key === "tender_bid_review_model_id") {
      const extraction = getCurrentModelId("tender_extraction_model_id", updates)
      if (extraction && usable(extraction)) return extraction
    }
    return tenderConfig?.default_model_ids[key] ?? ""
  }

  function handleModelChange(key: TenderModelKey, value: string) {
    setModelUpdates((current) => {
      const next = { ...current, [key]: value === SYSTEM_DEFAULT_VALUE ? "" : value }
      for (const field of TENDER_MODEL_FIELDS) {
        // An unset bid-review model follows the extraction model, so changing
        // extraction must also clear a now-incompatible bid-review effort.
        if (
          field.key !== key &&
          !(key === "tender_extraction_model_id" && field.key === "tender_bid_review_model_id")
        )
          continue
        const effort = getCurrentEffort(field.effortKey, next)
        const profile = tenderConfig?.reasoning_profiles[getEffectiveModelId(field.key, next)]
        if (effort && !profile?.supported_efforts.includes(effort)) next[field.effortKey] = ""
      }
      return next
    })
  }

  const saveMutation = useMutation({
    mutationFn: updateTenderModels,
    onSuccess: (data) => {
      toast.success(t("adminDashboard.tenderModels.saved", "Tender model configuration saved"), {
        description: data.message,
      })
      queryClient.setQueryData(adminQueryKeys.tenderModels(), data)
      setModelUpdates({})
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.tenderModels() })
    },
    onError: () => {
      toast.error(
        t("adminDashboard.tenderModels.saveError", "Failed to update tender model configuration")
      )
    },
  })

  function handleSave() {
    const normalizedUpdates = Object.fromEntries(
      Object.entries(modelUpdates).map(([key, value]) => [
        key,
        value === SYSTEM_DEFAULT_VALUE ? "" : value,
      ])
    ) as TenderModelUpdates
    saveMutation.mutate(normalizedUpdates)
  }

  const isLoading = configLoading || modelsLoading

  return (
    <CollapsibleManagementSection
      headerActions={<DelegationModeControl capability="tenders" />}
      icon={<Zap className="size-5" />}
      id="tender"
      isDirty={hasChanges}
      storageKey="admin-management-tender-expanded"
      title={t("adminDashboard.tender.title", "Tender")}
    >
      {isLoading || !tenderConfig || !models ? (
        <AdminSettingsLoadState
          loading={isLoading}
          retrying={configFetching || modelsFetching}
          onRetry={() => {
            if (!tenderConfig) void refetchConfig()
            if (!models) void refetchModels()
          }}
        />
      ) : (
        <div className="space-y-6">
          <h3 className="text-sm font-semibold">
            {t("adminDashboard.tenderModels.title", "Tender Models")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(
              "adminDashboard.tenderModels.description",
              "Configure the model and reasoning effort for each Tender workload. Standard settings preserve the current behavior."
            )}
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {TENDER_MODEL_FIELDS.map((field) => {
              const label = t(field.labelKey, field.labelDefault)
              const effort = getCurrentEffort(field.effortKey)
              const profile = tenderConfig.reasoning_profiles[getEffectiveModelId(field.key)]
              const efforts = profile?.supported_efforts ?? []
              const unavailableEffort = Boolean(effort && !efforts.includes(effort))
              const effortId = `${field.id}-effort`
              let effortDescription = t(
                "adminDashboard.tenderModels.effortDescription",
                "Higher effort can improve analysis but usually increases time and cost. Standard preserves current behavior."
              )
              if (!profile) {
                effortDescription = t(
                  "adminDashboard.tenderModels.effortModelUnavailable",
                  "Select an available model to configure effort."
                )
              } else if (efforts.length === 0) {
                effortDescription = t(
                  "adminDashboard.tenderModels.effortNotSupported",
                  "This model does not support reasoning effort."
                )
              }
              return (
                <div key={field.key} className="space-y-4">
                  <TenderModelSelect
                    description={t(field.descriptionKey, field.descriptionDefault)}
                    disabled={saveMutation.isPending}
                    id={field.id}
                    label={label}
                    models={EU_ONLY_KEYS.includes(field.key) ? euModels : enabledModels}
                    value={getCurrentModelId(field.key)}
                    formatUnavailable={(modelId) =>
                      t("adminDashboard.tenderModels.unavailableModel", {
                        defaultValue: "Unavailable: {{modelId}}",
                        modelId,
                      })
                    }
                    systemDefaultLabel={t(
                      "adminDashboard.tenderModels.systemDefault",
                      "(System default)"
                    )}
                    onValueChange={(value) => handleModelChange(field.key, value)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor={effortId}>
                      {t("adminDashboard.tenderModels.reasoningEffort", "Reasoning effort")}
                    </Label>
                    <Select
                      disabled={saveMutation.isPending || (!effort && efforts.length === 0)}
                      value={effort || SYSTEM_DEFAULT_VALUE}
                      onValueChange={(value) =>
                        setModelUpdates((current) => ({
                          ...current,
                          [field.effortKey]: value === SYSTEM_DEFAULT_VALUE ? "" : value,
                        }))
                      }
                    >
                      <SelectTrigger
                        aria-label={`${label}: ${t("adminDashboard.tenderModels.reasoningEffort", "Reasoning effort")}`}
                        id={effortId}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SYSTEM_DEFAULT_VALUE}>
                          {t(
                            "adminDashboard.tenderModels.effortDefault",
                            "Standard (current behavior)"
                          )}
                        </SelectItem>
                        {unavailableEffort && (
                          <SelectItem disabled value={effort}>
                            {t("adminDashboard.tenderModels.unavailableEffort", {
                              defaultValue: "Unsupported saved level: {{effort}}",
                              effort,
                            })}
                          </SelectItem>
                        )}
                        {efforts.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">{effortDescription}</p>
                  </div>
                </div>
              )
            })}
          </div>

          {hasChanges && (
            <Button disabled={saveMutation.isPending} size="sm" onClick={handleSave}>
              {saveMutation.isPending
                ? t("common.saving", "Saving...")
                : t("adminDashboard.tenderModels.save", "Save Changes")}
            </Button>
          )}

          <Separator />

          <TenderHandoffConfigContent />
        </div>
      )}
    </CollapsibleManagementSection>
  )
}
